import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import PageTitle from "../../layouts/PageTitle";
import SearchBox from "../../components/ui/SearchBox";
import Table from "../../components/ui/Table";
import Pagination from "../../components/ui/Pagination";
import ActivityLogFilterModal from "./ActivityLogFilterModal";
import ActivityLogDetailModal from "./ActivityLogDetailModal";

import { fetchActivityLogs } from "../../../store/custom-actions/activity-logs-actions/activityLogsSlicer";

function subjectTypeLabel(type) {
  if (!type) return "—";
  const parts = String(type).split("\\");
  return parts[parts.length - 1] || type;
}

export default function ActivityLogsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const debounceRef = useRef();

  const { data: paginator, loading } = useSelector(
    (state) => state.activityLogsSlicer
  );

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLog, setDetailLog] = useState(null);

  const [filters, setFilters] = useState({
    causer_id: "",
    subject_type: "",
    subject_id: "",
    date_from: "",
    date_to: "",
    search: "",
    page: 1,
    per_page: 15,
  });

  const list = paginator?.data ?? [];
  const totalItems = paginator?.total ?? 0;
  const currentPage = paginator?.current_page ?? 1;
  const perPage = paginator?.per_page ?? filters.per_page;
  const totalPages = paginator?.last_page ?? 1;

  const buildQuery = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value != null) params.append(key, value);
    });
    return `?${params.toString()}`;
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      dispatch(fetchActivityLogs(buildQuery()));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [filters]);

  const handlePagination = (page) =>
    setFilters((prev) => ({ ...prev, page: page + 1 }));

  const handleSearch = (value) =>
    setFilters((prev) => ({ ...prev, search: value, page: 1 }));

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterModal(false);
  };

  const openDetail = (log) => {
    setDetailLog(log);
    setShowDetailModal(true);
  };

  const columns = [
    {
      header: t("Description"),
      render: (log) => (
        <span
          className="text-primary cursor-pointer"
          onClick={() => openDetail(log)}
          role="button"
          onKeyDown={(e) => e.key === "Enter" && openDetail(log)}
          tabIndex={0}
        >
          {log.description || "—"}
        </span>
      ),
    },
    {
      header: t("Causer"),
      render: (log) =>
        log.causer
          ? log.causer.name || log.causer.email || `#${log.causer_id}`
          : `#${log.causer_id}`,
    },
    {
      header: t("Subject Type"),
      render: (log) => subjectTypeLabel(log.subject_type),
    },
    {
      header: t("Subject ID"),
      render: (log) => log.subject_id ?? "—",
    },
    {
      header: t("Date"),
      render: (log) =>
        log.created_at
          ? new Date(log.created_at).toLocaleString()
          : "—",
    },
  ];

  return (
    <>
      <PageTitle motherMenu={t("Dashboard")} activeMenu={t("Activity Logs")} />

      <div className="col-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <SearchBox
              searchValue={filters.search}
              setSearchValue={(v) =>
                setFilters((prev) => ({ ...prev, search: v }))
              }
              onSearch={handleSearch}
              placeholder={t("Search in description...")}
              delay={500}
            />
            <Button
              variant="primary"
              onClick={() => setShowFilterModal(true)}
            >
              {t("Filter")}
            </Button>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <Table
                columns={columns}
                data={list}
                loading={loading.fetch}
                emptyText={t("No activity logs found")}
                showIndex
                startIndex={(currentPage - 1) * perPage}
              />
            </div>
            {totalPages > 1 && (
              <Pagination
                totalItems={totalItems}
                currentPage={currentPage - 1}
                perPage={perPage}
                onPageChange={handlePagination}
                t={t}
              />
            )}
          </div>
        </div>
      </div>

      <ActivityLogFilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        filters={filters}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
      />

      <ActivityLogDetailModal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        log={detailLog}
      />
    </>
  );
}
